import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';

import { initializeNotifications } from '../../config/toast-notifications.js';
import ToastContainer from '../components/ui/toast-container.js';
import { ToastType } from '../components/ui/toast-notification.js';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  persistent?: boolean;
}

interface ToastContextType {
  showToast: (type: ToastType, title: string, message?: string, duration?: number, persistent?: boolean) => void;
  showSuccess: (title: string, message?: string, persistent?: boolean) => void;
  showError: (title: string, message?: string, persistent?: boolean) => void;
  showWarning: (title: string, message?: string, persistent?: boolean) => void;
  showInfo: (title: string, message?: string, persistent?: boolean) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const generateId = () => {
    return `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const showToast = useCallback((
    type: ToastType,
    title: string,
    message?: string,
    duration: number = type === 'error' ? 3000 : 2000,
    persistent: boolean = false
  ) => {
    const id = generateId();
    const newToast: Toast = {
      id,
      type,
      title,
      message,
      duration: persistent ? 0 : duration,
      persistent
    };

    setToasts(prev => [...prev, newToast]);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showSuccess = useCallback((title: string, message?: string, persistent: boolean = false) => {
    showToast('success', title, message, 2000, persistent);
  }, [showToast]);

  const showError = useCallback((title: string, message?: string, persistent: boolean = false) => {
    showToast('error', title, message, 3000, persistent);
  }, [showToast]);

  const showWarning = useCallback((title: string, message?: string, persistent: boolean = false) => {
    showToast('warning', title, message, 2500, persistent);
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string, persistent: boolean = false) => {
    showToast('info', title, message, 2000, persistent);
  }, [showToast]);

  const contextValue = useMemo<ToastContextType>(() => ({
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo
  }), [showToast, showSuccess, showError, showWarning, showInfo]);

  // Initialize the global notifications config with this context
  useEffect(() => {
    initializeNotifications({
      showSuccess,
      showError,
      showWarning,
      showInfo
    });
  }, [showSuccess, showError, showWarning, showInfo]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
};

export default ToastContext;
